/**
 * @file components/log/WorkoutTracker.jsx
 * @description Refactored & Optimized: Monthly consistency grid with Optimistic UI & Scoped State.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Target,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useProfile } from "../../hooks/useProfile";
import { useWorkoutMutations } from "../../hooks/useWorkoutMutations";
import { useWorkouts } from "../../hooks/useWorkouts";
import { trackWorkoutLogged } from "../../services/analytics";
import { updateProfileData } from "../../services/firebase";
import { useAuth } from "../../utils/AuthContext";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Creates a unique string key for a specific date to prevent multi-month "bleeding"
 */
const formatDateKey = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

/**
 * Calculates calendar grid data and month constraints
 */
const getCalendarData = (monthDate) => {
  const year = monthDate.getFullYear();
  const monthIdx = monthDate.getMonth();

  // Day 0 of next month is the last day of the current month
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const firstDay = new Date(year, monthIdx, 1).getDay();

  const dayGrid = Array(firstDay)
    .fill(null)
    .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

  return { daysInMonth, dayGrid, year, monthIdx };
};

// ============================================================================
// MEMOIZED SUB-COMPONENTS
// ============================================================================

const StatCard = memo(
  ({ icon, label, value, unit, color, isClickable, onClick }) => (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`bg-white/[0.02] border border-white/5 rounded-2xl p-4 transition-all ${
        isClickable
          ? "hover:bg-white/[0.04] hover:border-white/10 cursor-pointer active:scale-95"
          : ""
      }`}
    >
      <div className="flex items-center gap-2 mb-2 opacity-60">
        {icon}
        <span className="text-[8px] uppercase tracking-widest font-bold">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${color}`}>{value}</span>
        <span className="text-[8px] text-gray-600 uppercase font-bold">
          {unit}
        </span>
      </div>
    </div>
  ),
);
StatCard.displayName = "StatCard"; // Fixes the ESLint error

const CalendarDayButton = memo(({ day, isLogged, isLoading, onToggle }) => {
  if (!day) return <div className="opacity-0 cursor-default" />;

  return (
    <button
      onClick={() => onToggle(day)}
      className={`aspect-square rounded-xl text-xs font-bold transition-all ${
        isLogged
          ? "bg-brand-green text-[#0B0B0B] shadow-[0_0_15px_rgba(16,185,129,0.2)]"
          : "bg-white/[0.02] border border-white/5 text-gray-400 hover:border-brand-green/40"
      } ${isLoading ? "animate-pulse" : "active:scale-90"}`}
    >
      {day}
    </button>
  );
});
CalendarDayButton.displayName = "CalendarDayButton"; // Fixes the ESLint error

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WorkoutTracker({ onShowAuth }) {
  const { uid, isGuest } = useAuth();
  const queryClient = useQueryClient();

  // --- STATE ---
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempGoal, setTempGoal] = useState(20);
  const [saveLoading, setSaveLoading] = useState(false);
  const [localWorkoutDates, setLocalWorkoutDates] = useState(new Set());

  const debounceTimer = useRef(null);

  // --- DATA HOOKS ---
  const { data: profileData = {} } = useProfile(uid);
  const { data: workoutDays = new Set(), isLoading: serverLoading } =
    useWorkouts(uid, currentMonth);
  const toggleMutation = useWorkoutMutations(uid);

  // --- MEMOIZED CALENDAR LOGIC ---
  const { daysInMonth, dayGrid, year, monthIdx } = useMemo(
    () => getCalendarData(currentMonth),
    [currentMonth],
  );

  const monthName = useMemo(
    () =>
      currentMonth.toLocaleString("default", {
        month: "long",
        year: "numeric",
      }),
    [currentMonth],
  );

  // --- GUEST GUARD ---
  if (isGuest) {
    return (
      <div className="text-center py-20 px-6 bg-white/[0.02] rounded-3xl border border-dashed border-white/10">
        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
          <Target className="text-gray-600" size={24} />
        </div>
        <h3 className="text-white font-bold text-lg">Track your workouts</h3>
        <p className="text-gray-500 text-sm mt-1 mb-5">
          Sign in to log workouts, set goals, and track your consistency.
        </p>
        <button
          onClick={onShowAuth}
          className="px-8 py-3 rounded-xl bg-brand-green text-brand-bg font-bold text-xs uppercase tracking-widest hover:bg-green-400 transition-colors"
        >
          Sign in to start
        </button>
      </div>
    );
  }

  // --- SYNC LOCAL STATE WITH SERVER TRUTH ---
  useEffect(() => {
    const serverDates = new Set(
      Array.from(workoutDays).map((day) => formatDateKey(year, monthIdx, day)),
    );
    setLocalWorkoutDates(serverDates);
  }, [workoutDays, year, monthIdx]);

  // --- HANDLERS ---
  const handleMonthChange = useCallback((offset) => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1),
    );
  }, []);

  const handleToggleWorkout = useCallback(
    (day) => {
      if (!uid) return;

      const dateKey = formatDateKey(year, monthIdx, day);
      const newDates = new Set(localWorkoutDates);
      const isAdding = !newDates.has(dateKey);

      // 1. Optimistic Update (Immediate Feedback)
      if (isAdding) newDates.add(dateKey);
      else newDates.delete(dateKey);
      setLocalWorkoutDates(newDates);

      // 2. Debounce Firebase Call (500ms)
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(() => {
        const wasOriginallyLogged = workoutDays.has(day);

        // Only hit the network if the current toggle state differs from the database
        if (isAdding !== wasOriginallyLogged) {
          toggleMutation.mutate(
            { day, currentMonth, isDelete: !isAdding },
            {
              onSuccess: () => {
                if (isAdding)
                  trackWorkoutLogged(uid, new Date(year, monthIdx, day));
              },
              onError: () => {
                // Rollback UI on network error
                setLocalWorkoutDates(
                  new Set(
                    Array.from(workoutDays).map((d) =>
                      formatDateKey(year, monthIdx, d),
                    ),
                  ),
                );
              },
            },
          );
        }
      }, 500);
    },
    [
      uid,
      year,
      monthIdx,
      localWorkoutDates,
      workoutDays,
      currentMonth,
      toggleMutation,
    ],
  );

  const handleSaveGoal = useCallback(async () => {
    if (!uid || tempGoal < 1 || tempGoal > daysInMonth) return;
    setSaveLoading(true);
    try {
      await updateProfileData(uid, { monthlyGoal: tempGoal });
      await queryClient.invalidateQueries({ queryKey: ["profile", uid] });
      setIsModalOpen(false);
    } catch (err) {
      console.error("Goal save failed:", err);
    } finally {
      setSaveLoading(false);
    }
  }, [uid, tempGoal, daysInMonth, queryClient]);

  // --- STATS CALCS ---
  const monthlyGoal = profileData.monthlyGoal || 20;
  const workoutCount = localWorkoutDates.size;
  const workoutPercentage = Math.min(
    Math.round((workoutCount / monthlyGoal) * 100),
    100,
  );

  return (
    <div className="max-w-2xl mx-auto px-4 animate-fadeIn">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-green font-bold mb-2">
          Consistency Engine
        </p>
        <h2 className="font-serif text-3xl text-white tracking-tight">
          Workout Log
        </h2>
      </header>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={<Target size={16} className="text-brand-green" />}
          label="Completion"
          value={`${workoutPercentage}%`}
          unit="of goal"
          color="text-brand-green"
        />
        <StatCard
          icon={<TrendingUp size={16} className="text-emerald-400" />}
          label="Sessions"
          value={workoutCount}
          unit={monthName.split(" ")[0]}
          color="text-emerald-400"
        />
        <StatCard
          icon={<Trophy size={16} className="text-brand-amber" />}
          label="Monthly Goal"
          value={monthlyGoal}
          unit="sessions"
          color="text-brand-amber"
          isClickable
          onClick={() => {
            setTempGoal(monthlyGoal);
            setIsModalOpen(true);
          }}
        />
      </div>

      {/* Calendar Grid Container */}
      {/* Adding a key to the container forces a clean transition between months */}
      <div
        key={monthName}
        className="bg-white/[0.03] border border-white/5 rounded-[2rem] p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => handleMonthChange(-1)}
            className="p-2 hover:bg-white/5 rounded-full transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="font-bold text-sm uppercase tracking-widest">
            {monthName}
          </h3>
          <button
            onClick={() => handleMonthChange(1)}
            className="p-2 hover:bg-white/5 rounded-full transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 text-center mb-4 text-[10px] font-bold text-gray-600">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, idx) => (
            <span key={`day-header-${idx}`}>{d}</span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {dayGrid.map((day, idx) => (
            <CalendarDayButton
              // Combined key ensures uniqueness even if the month changes
              key={`${monthName}-grid-day-${idx}`}
              day={day}
              isLogged={
                day
                  ? localWorkoutDates.has(formatDateKey(year, monthIdx, day))
                  : false
              }
              isLoading={serverLoading}
              onToggle={handleToggleWorkout}
            />
          ))}
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex justify-between items-end mb-2 px-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
          Target Progress
        </p>
        <p className="text-xs font-serif italic text-brand-green">
          {workoutPercentage}%
        </p>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-12">
        <div
          className="h-full bg-brand-green transition-all duration-700 ease-out"
          style={{ width: `${workoutPercentage}%` }}
        />
      </div>

      {/* Goal Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-xl"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="relative bg-[#0F1115] border border-white/10 w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 pb-4 flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-brand-amber/10 rounded-2xl text-brand-amber">
                  <Trophy size={20} />
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-white tracking-tight">
                    Monthly Goal
                  </h2>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                    Set your target
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-white/5 rounded-full text-gray-500 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-8 pt-4 space-y-6">
              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400 block">
                  Sessions per month
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setTempGoal(Math.max(1, tempGoal - 1))}
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={tempGoal}
                    onChange={(e) =>
                      setTempGoal(
                        Math.max(
                          1,
                          Math.min(daysInMonth, parseInt(e.target.value) || 1),
                        ),
                      )
                    }
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-lg font-bold focus:border-brand-amber focus:outline-none"
                  />
                  <button
                    onClick={() =>
                      setTempGoal(Math.min(daysInMonth, tempGoal + 1))
                    }
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-4 rounded-xl border border-white/10 text-gray-400 font-bold text-xs uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGoal}
                  disabled={saveLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-brand-amber text-[#0B0B0B] font-bold text-xs uppercase tracking-widest disabled:opacity-30"
                >
                  {saveLoading ? "..." : "Save Goal"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
